import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import './CreateServer.css'
import { Gamepad2, Server, Settings, CheckCircle, ArrowRight, ArrowLeft, Crown, Cpu, HardDrive, Zap } from 'lucide-react'

const GAMES = [
  { id: 'Minecraft', name: 'Minecraft', description: 'Survival, Creative, Mods' },
  { id: 'Counter Strike', name: 'Counter-Strike', description: '1.6, Source' },
  { id: 'Terraria', name: 'Terraria', description: 'Vanilla, Mods' },
]

const getGameImage = (id: string) => {
  const slug = id.toLowerCase().replace(/\s+/g, '-')
  return `/assets/create/${slug}.webp`
}

interface PlanInfo {
  id: string
  label: string
  description: string
  architecture: string
  location: string
  binary: string
  price: number
  ram: number
}

interface VersionOption {
  id: string
  name: string
  popular: boolean
}

type Step = 'game' | 'plan' | 'config' | 'review'

export function CreateServer() {
  const navigate = useNavigate()
  const { state, fetchDashboardData } = useApp()

  const [step, setStep] = useState<Step>('game')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [selectedGame, setSelectedGame] = useState<string>('')
  const [selectedPlan, setSelectedPlan] = useState<PlanInfo | null>(null)
  const [serverName, setServerName] = useState('')
  const [serverType, setServerType] = useState('')
  const [serverVersion, setServerVersion] = useState('')

  // Data from API
  const [plans, setPlans] = useState<PlanInfo[]>([])
  const [types, setTypes] = useState<string[]>([])

  // Derive versions from serverType
  const versions = useMemo(() => {
    if (selectedGame && serverType && state.versions) {
      const gameVersions = state.versions[selectedGame]
      if (gameVersions && gameVersions[serverType]) {
        return gameVersions[serverType] as VersionOption[]
      }
    }
    return []
  }, [selectedGame, serverType, state.versions])

  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'game', label: 'Juego', icon: <Gamepad2 size={18} /> },
    { key: 'plan', label: 'Plan', icon: <Crown size={18} /> },
    { key: 'config', label: 'Configurar', icon: <Settings size={18} /> },
    { key: 'review', label: 'Revisar', icon: <CheckCircle size={18} /> },
  ]

  useEffect(() => {
    let cancelled = false

    const init = async () => {
      if (state.user) {
        setLoading(false)
        return
      }

      try {
        await fetchDashboardData()
      } catch {
        if (!cancelled) navigate('/')
        return
      }

      if (!cancelled) {
        setLoading(false)
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [])

  // Load plans when game is selected
  useEffect(() => {
    if (selectedGame) {
      fetchPlans(selectedGame)
    }
  }, [selectedGame])

  // Load versions and types when game or plan changes
  useEffect(() => {
    if (selectedGame && state.versions) {
      const gameVersions = state.versions[selectedGame]
      if (gameVersions) {
        const availableTypes = Object.keys(gameVersions).filter(k => k !== 'saveData' && k !== 'plans')
        setTypes(availableTypes)

        // Set default type and version
        const defaultType = availableTypes[0] || ''
        setServerType(defaultType)

        if (defaultType && gameVersions[defaultType]) {
          const typeVersions = gameVersions[defaultType] as VersionOption[]
          if (typeVersions.length > 0) {
            setServerVersion(typeVersions[0].id)
          }
        }
      }
    }
  }, [selectedGame, state.versions])

  // Update version when serverType changes (for Counter Strike)
  useEffect(() => {
    if (selectedGame === 'Counter Strike' && serverType && state.versions) {
      const gameVersions = state.versions[selectedGame]
      if (gameVersions && gameVersions[serverType]) {
        const typeVersions = gameVersions[serverType] as VersionOption[]
        if (typeVersions.length > 0) {
          setServerVersion(typeVersions[0].id)
        }
      }
    }
  }, [serverType, selectedGame, state.versions])

  const fetchPlans = async (game: string) => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/plans/${encodeURIComponent(game)}`)
      const data = await res.json()
      if (data.plans) {
        setPlans(data.plans)
        if (data.plans.length > 0 && !selectedPlan) {
          setSelectedPlan(data.plans[0])
        }
      }
    } catch (err) {
      console.error('Error fetching plans:', err)
    }
  }

  const canProceed = (): boolean => {
    switch (step) {
      case 'game':
        return !!selectedGame
      case 'plan':
        return !!selectedPlan
      case 'config':
        return !!serverName.trim() && !!serverVersion
      case 'review':
        return true
      default:
        return false
    }
  }

  const nextStep = () => {
    const order: Step[] = ['game', 'plan', 'config', 'review']
    const currentIndex = order.indexOf(step)
    if (currentIndex < order.length - 1) {
      setStep(order[currentIndex + 1])
    }
  }

  const prevStep = () => {
    const order: Step[] = ['game', 'plan', 'config', 'review']
    const currentIndex = order.indexOf(step)
    if (currentIndex > 0) {
      setStep(order[currentIndex - 1])
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setError(null)

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: serverName,
          game: selectedGame,
          type: serverType,
          version: serverVersion,
          maxPlayers: 20,
        }),
        credentials: 'include',
      })

      const result = await res.json()

      if (res.ok) {
        navigate(`/server/${result.server.id}`)
      } else {
        setError(result.error || 'Error al crear servidor')
        setCreating(false)
      }
    } catch (err) {
      setError('Error de conexión')
      setCreating(false)
    }
  }

  const formatPrice = (price: number) => {
    return price.toFixed(2) // Hourly price
  }

  if (loading) {
    return (
      <div className="create-server-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="create-server-page">
      <button className="back-button" onClick={() => navigate('/dashboard')}>
        <ArrowLeft size={20} />
        <span>Volver</span>
      </button>
      <div className="create-server-container">
        {/* Header */}
        <div className="wizard-header">
          <h1>Crear Servidor</h1>
        </div>

        {/* Progress Steps */}
        <div className="wizard-steps">
          {steps.map((s, index) => {
            const order: Step[] = ['game', 'plan', 'config', 'review']
            const currentIndex = order.indexOf(step)
            const stepIndex = order.indexOf(s.key)
            const isActive = step === s.key
            const isCompleted = stepIndex < currentIndex

            return (
              <div
                key={s.key}
                className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="step-icon">{s.icon}</div>
                <span className="step-label">{s.label}</span>
                {index < steps.length - 1 && <div className="step-connector" />}
              </div>
            )
          })}
        </div>

        {/* Error Message */}
        {error && <div className="wizard-error">{error}</div>}

        {/* Step Content */}
        <div className="wizard-content">
          {/* Step 1: Game Selection */}
          {step === 'game' && (
            <div className="step-game">
              <div className="step-header">
                <p className="step-header-label">Selecciona tu juego</p>
                <p className="step-description">Elige el juego para el que quieres crear un servidor</p>
              </div>
              <div className="game-cards">
                {GAMES.map(game => (
                  <button
                    key={game.id}
                    className={`game-card ${selectedGame === game.id ? 'selected' : ''}`}
                    onClick={() => setSelectedGame(game.id)}
                  >
                    <div className="game-image-wrapper">
                      <img
                        src={getGameImage(game.id)}
                        alt={game.name}
                        className="game-image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.parentElement!.classList.add('no-image')
                        }}
                      />
                      {selectedGame === game.id && <div className="game-selected-badge">✓</div>}
                    </div>
                    <span className="game-name">{game.name}</span>
                    <span className="game-description">{game.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Plan Selection */}
          {step === 'plan' && (
            <div className="step-plan">
              <h2>Selecciona tu plan</h2>
              <p className="step-description">Elige los recursos que tendrá tu servidor</p>
              <div className="plan-cards">
                {plans.map(plan => (
                  <button
                    key={plan.id}
                    className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <div className="plan-header">
                      <span className="plan-icon"><Crown size={24} /></span>
                      <span className="plan-label">{plan.label}</span>
                    </div>
                    <div className="plan-specs">
                      <div className="spec">
                        <HardDrive size={16} />
                        <span>{plan.ram}GB RAM</span>
                      </div>
                      <div className="spec">
                        <Cpu size={16} />
                        <span>{plan.description.split('|')[1]?.trim() || plan.description}</span>
                      </div>
                    </div>
                    <div className="plan-price">
                      <span className="price-value">${formatPrice(plan.price)}</span>
                      <span className="price-period">/hora</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Configuration */}
          {step === 'config' && (
            <div className="step-config">
              <h2>Configura tu servidor</h2>
              <p className="step-description">Personaliza los detalles de tu nuevo servidor</p>
              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="serverName">Nombre del servidor</label>
                  <input
                    id="serverName"
                    type="text"
                    value={serverName}
                    onChange={e => setServerName(e.target.value)}
                    placeholder="Ej: Mi Servidor de Minecraft"
                    maxLength={50}
                  />
                  <span className="config-help">Este nombre se mostrará en tu panel</span>
                </div>

                {types.length > 1 && (
                  <div className="form-group">
                    <label>Tipo de servidor</label>
                    <div className="type-buttons">
                      {types.map(type => (
                        <button
                          key={type}
                          type="button"
                          className={`type-btn ${serverType === type ? 'active' : ''}`}
                          onClick={() => setServerType(type)}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                    <span className="config-help">Elige el tipo de experiencia que quieres</span>
                  </div>
                )}

                {/* Only show version selector for games that have multiple versions (not Counter Strike) */}
                {selectedGame !== 'Counter Strike' && (
                  <div className="form-group">
                    <label htmlFor="version">Versión del juego</label>
                    <select
                      id="version"
                      value={serverVersion}
                      onChange={e => setServerVersion(e.target.value)}
                    >
                      {versions.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.popular ? '⭐' : ''}
                        </option>
                      ))}
                    </select>
                    <span className="config-help">Selecciona la versión de {selectedGame} para tu servidor</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 'review' && (
            <div className="step-review">
              <h2>Revisa tu servidor</h2>
              <p className="step-description">Verifica que todo esté correcto antes de crear</p>
              <div className="review-card">
                <div className="review-row">
                  <span className="review-label">Juego</span>
                  <span className="review-value">{selectedGame}</span>
                </div>
                <div className="review-row">
                  <span className="review-label">Plan</span>
                  <span className="review-value">{selectedPlan?.label}</span>
                </div>
                <div className="review-row">
                  <span className="review-label">Recursos</span>
                  <span className="review-value">{selectedPlan?.description}</span>
                </div>
                <div className="review-row">
                  <span className="review-label">Nombre</span>
                  <span className="review-value">{serverName}</span>
                </div>
                <div className="review-row">
                  <span className="review-label">Tipo</span>
                  <span className="review-value">{serverType}</span>
                </div>
                {selectedGame !== 'Counter Strike' && (
                  <div className="review-row">
                    <span className="review-label">Versión</span>
                    <span className="review-value">{serverVersion}</span>
                  </div>
                )}
                <div className="review-divider" />
                <div className="review-row total">
                  <span className="review-label">Precio por hora</span>
                  <span className="review-value price">${selectedPlan ? formatPrice(selectedPlan.price) : '0.00'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="wizard-nav">
          {step !== 'game' && (
            <button className="nav-btn back" onClick={prevStep}>
              <ArrowLeft size={18} />
              <span>Anterior</span>
            </button>
          )}
          <div className="nav-spacer" />
          {step !== 'review' ? (
            <button
              className="nav-btn next"
              onClick={nextStep}
              disabled={!canProceed()}
            >
              <span>Siguiente</span>
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              className="nav-btn create"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? 'Creando...' : 'Crear Servidor'}
              {!creating && <Zap size={18} />}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}